import { apiFetch, getErrorMessage } from './client';

interface PublicKeyResponse {
  key: string;
}

// getPublicKey returns null when push is disabled server-side (no VAPID
// keys configured) — the 404 is the "disabled" signal, not an error.
export async function getPublicKey(): Promise<string | null> {
  try {
    const { key } = await apiFetch<PublicKeyResponse>('/push/public_key');
    return key;
  } catch {
    return null;
  }
}

// urlBase64ToUint8Array converts the VAPID public key (base64url, no
// padding) into the Uint8Array pushManager.subscribe's applicationServerKey
// expects. Standard boilerplate for the Push API — no library does this.
function urlBase64ToUint8Array(base64url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    const reg = await readyServiceWorker();
    return reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

async function readyServiceWorker(): Promise<ServiceWorkerRegistration> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // Registration at app boot is best-effort, but punch-in depends on push.
    // Retry here so a transient boot failure has a visible, actionable error
    // instead of silently leaving navigator.serviceWorker.ready unresolved.
    if (!(await navigator.serviceWorker.getRegistration())) {
      await navigator.serviceWorker.register('/sw.js');
    }
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Notification service is not ready. Reload the app and try again.')), 5000);
      })
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

interface SubscribeResult {
  ok: boolean;
  error?: string;
  endpoint?: string;
}

export interface PunchInPushResult extends SubscribeResult {
  required: boolean;
}

// enablePush walks the full opt-in flow: ask permission, subscribe with the
// server's VAPID key, hand the subscription to the backend. Denial and
// "not supported" are handled gracefully (returned as {ok:false}, never
// thrown) so the caller can just show a message.
export async function enablePush(): Promise<SubscribeResult> {
  if (!pushSupported()) return { ok: false, error: 'Push notifications are not supported on this device.' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, error: 'Notification permission was not granted.' };
  }

  const key = await getPublicKey();
  if (!key) return { ok: false, error: 'Push notifications are not enabled on the server.' };

  try {
    const reg = await readyServiceWorker();
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // TS's lib.dom types disagree with themselves on Uint8Array vs.
        // BufferSource here (a known @types/web quirk); the runtime accepts
        // a plain Uint8Array fine.
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource
      }));

    const json = sub.toJSON();
    await apiFetch<null>('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: json.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth
      })
    });
    return { ok: true, endpoint: sub.endpoint };
  } catch (err) {
    return { ok: false, error: getErrorMessage(err) };
  }
}

export async function testPushNotification(): Promise<void> {
  await apiFetch('/push/test', { method: 'POST' });
}

// Production punch-in enforcement is activated by the same server key, so
// only prompt (and require an endpoint) when that enforcement is enabled.
export async function activatePushForPunchIn(key: string | null): Promise<PunchInPushResult> {
  if (!key) return { ok: true, required: false };

  if (!pushSupported()) {
    return { ok: false, required: true, error: 'Push notifications are not supported on this device.' };
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, required: true, error: 'Notification permission was not granted.' };
  }

  try {
    const reg = await readyServiceWorker();
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource
      }));
    const json = sub.toJSON();
    await apiFetch<null>('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint: json.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth })
    });
    return { ok: true, required: true, endpoint: sub.endpoint };
  } catch (err) {
    return { ok: false, required: true, error: getErrorMessage(err) };
  }
}
